import React, { useContext } from 'react';
import { useParams } from 'react-router-dom';
import { AppContext } from '../AppContext';
import { Card } from 'primereact/card';

function OverviewExportPage() {
  const { customerId } = useParams();
  const { customers } = useContext(AppContext);
  const customer = customers.find(c => c.id === customerId) || {};
  const serviceData = customer.serviceData || {};
  
  // Aggregate servers from all sites
  const sites = serviceData.sites || [];
  const servers = sites.reduce((acc, site) => {
    return acc.concat(site.servers || []);
  }, []);
  
  // Default retention period for DRaaS/BaaS calculations
  const retentionPeriod = 1;

  // --- Filter VMs for each service ---
  const iaaSVMs = servers.filter(s => s.services?.IaaS);
  const draaSVMs = servers.filter(s => s.services?.DRaaS);
  const baaSVMs = servers.filter(s => s.services?.BaaS);

  // --- IaaS Calculations ---
  const iaaSVMCount = iaaSVMs.length;
  const totalvCPU = iaaSVMs.reduce((acc, s) => acc + Number(s['CPUs'] || 0), 0);
  const totalMemory = iaaSVMs.reduce((acc, s) => acc + Number(s['Memory'] || 0), 0);
  // For storage, assume the field "In Use MiB" represents used storage in MiB.
  const totalStorage = iaaSVMs.reduce((acc, s) => {
    const diskGB = Number(s['In Use MiB'] || 0);
    return acc + diskGB / 1000;
  }, 0);

  // --- DRaaS Calculations ---
  const draaSVMCount = draaSVMs.length;
  const draaSStorage = draaSVMs.reduce((acc, s) => {
    const diskGB = Number(s['In Use MiB'] || 0);
    return acc + ((diskGB * retentionPeriod) / 1000);
  }, 0);

  // --- BaaS Calculations ---
  const baaSVMCount = baaSVMs.length;
  const baaSStorage = baaSVMs.reduce((acc, s) => {
    const diskGB = Number(s['In Use MiB'] || 0);
    return acc + ((diskGB * retentionPeriod) / 1000);
  }, 0);

  // --- Additional Metrics ---
  // For SQL Licenses: Sum the number of cores for servers with sqlLicenses enabled, then divide by 2 and round up.
  const totalCoresForSQL = servers
    .filter(s => s.sqlLicenses)
    .reduce((acc, s) => acc + Number(s['CPUs'] || 0), 0);
  const sqlLicensePacks = Math.ceil(totalCoresForSQL / 2);

  // Microsoft Licenses: Count servers with microsoftLicense flag.
  const microsoftLicenses = servers.filter(s => s.microsoftLicense).length;
  // Public IPs: Count servers where publicIp is true.
  const publicIPs = servers.filter(s => s.publicIp).length;
  const sslVpnLicenses = serviceData.sslVpnUsers || 0;

  // Overview data for display
  const overview = {
    kpiData: {
      iaaSVMCount,
      totalvCPU,
      totalMemory,
      totalStorage,
      draaSVMCount,
      draaSStorage,
      baaSVMCount,
      baaSStorage,
      sqlLicenses: sqlLicensePacks,
      sslVpnLicenses,
      microsoftLicenses,
      publicIPs,
    },
  };

  return (
    <div className="p-4">
      <h2>Configuration Overview</h2>
      <div className="card p-4 mb-4">
        <h3>IaaS</h3>
        <div className="flex gap-4">
          <Card title="Number of VMs">
            <p>{overview.kpiData.iaaSVMCount}</p>
          </Card>
          <Card title="Total vCPU">
            <p>{overview.kpiData.totalvCPU}</p>
          </Card>
          <Card title="Total Memory">
            <p>{overview.kpiData.totalMemory} GB</p>
          </Card>
          <Card title="Total Storage">
            <p>{overview.kpiData.totalStorage.toFixed(3)} TiB</p>
          </Card>
        </div>
        <h3>DRaaS</h3>
        <div className="flex gap-4">
          <Card title="Number of VMs">
            <p>{overview.kpiData.draaSVMCount}</p>
          </Card>
          <Card title="Storage">
            <p>{overview.kpiData.draaSStorage.toFixed(3)} TiB</p>
          </Card>
        </div>
        <h3>BaaS</h3>
        <div className="flex gap-4">
          <Card title="Number of VMs">
            <p>{overview.kpiData.baaSVMCount}</p>
          </Card>
          <Card title="Storage">
            <p>{overview.kpiData.baaSStorage.toFixed(3)} TiB</p>
          </Card>
        </div>
        <h3>Additional Metrics</h3>
        <div className="flex gap-4">
          <Card title="SQL Licenses (2-Core Packs)">
            <p>{overview.kpiData.sqlLicenses}</p>
          </Card>
          <Card title="SSL VPN Licenses">
            <p>{overview.kpiData.sslVpnLicenses}</p>
          </Card>
          <Card title="Microsoft Licenses">
            <p>{overview.kpiData.microsoftLicenses}</p>
          </Card>
          <Card title="Public IPs">
            <p>{overview.kpiData.publicIPs}</p>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default OverviewExportPage;
